import {
  resolveExclusiveDomainScopeFlags,
  scopeFlagsFromMode,
} from '@terra/shared-asset-processing';
import {
  getScopePathInputValidationError,
  normalizeScopePathInput,
} from '@terra/shared-contracts-applications';
import type { PathDraftRowState } from './AddPathDraftRow';
import type { PathScopeDraft, PathScopeKind } from './domain-scope.utils';

export type AddPathsValidationMessages = {
  incompleteRowError: string;
  pathContainsSpacesError: string;
  pathInvalidCharactersError: string;
  pathInvalidStructureError: string;
  duplicatePathError: string;
};

const scopePathValidationErrorToMessage = ({
  error,
  messages,
}: {
  error: NonNullable<ReturnType<typeof getScopePathInputValidationError>>;
  messages: AddPathsValidationMessages;
}): string => {
  switch (error) {
    case 'containsSpaces':
      return messages.pathContainsSpacesError;
    case 'invalidCharacters':
      return messages.pathInvalidCharactersError;
    case 'invalidStructure':
      return messages.pathInvalidStructureError;
  }
};

export const getPathDraftRowPathError = ({
  row,
  draftRows,
  reservedPaths,
  messages,
  showEmptyErrors,
}: {
  row: PathDraftRowState;
  draftRows: PathDraftRowState[];
  reservedPaths: string[];
  messages: AddPathsValidationMessages;
  showEmptyErrors: boolean;
}): string | null => {
  const trimmedPath = row.path.trim();

  if (trimmedPath.length === 0) {
    if (showEmptyErrors || row.kind !== null) {
      return messages.incompleteRowError;
    }

    return null;
  }

  const pathValidationError = getScopePathInputValidationError(trimmedPath);

  if (pathValidationError) {
    return scopePathValidationErrorToMessage({
      error: pathValidationError,
      messages,
    });
  }

  const normalizedPath = normalizeScopePathInput(trimmedPath);

  const hasDuplicateInForm = draftRows.some((otherRow) => {
    if (otherRow.id === row.id) {
      return false;
    }

    const otherTrimmedPath = otherRow.path.trim();

    if (otherTrimmedPath.length === 0) {
      return false;
    }

    return normalizeScopePathInput(otherTrimmedPath) === normalizedPath;
  });

  if (hasDuplicateInForm || reservedPaths.includes(normalizedPath)) {
    return messages.duplicatePathError;
  }

  return null;
};

export const buildPathsFromDraftRows = (
  draftRows: PathDraftRowState[],
  options?: { isPrimaryHost?: boolean },
): PathScopeDraft[] =>
  draftRows.map((row) => {
    const kind = row.kind as PathScopeKind;
    const isThirdParty = options?.isPrimaryHost ? false : row.isThirdParty;

    if (kind === 'blocklist') {
      return {
        path: normalizeScopePathInput(row.path.trim()),
        kind,
        isInFunctionalityScope: false,
        isInTestingScope: false,
        isThirdParty: false,
      };
    }

    return {
      path: normalizeScopePathInput(row.path.trim()),
      kind,
      isInFunctionalityScope: row.isInTestingScope
        ? true
        : row.isInFunctionalityScope,
      isInTestingScope: row.isInTestingScope,
      isThirdParty,
    };
  });

export const canSubmitPathDraftRows = ({
  draftRows,
  reservedPaths,
}: {
  draftRows: PathDraftRowState[];
  reservedPaths: string[];
}): boolean => {
  if (draftRows.length === 0) {
    return false;
  }

  const seenPaths = new Set<string>();

  for (const row of draftRows) {
    const trimmedPath = row.path.trim();

    if (trimmedPath.length === 0 || row.kind === null) {
      return false;
    }

    if (getScopePathInputValidationError(trimmedPath)) {
      return false;
    }

    const normalizedPath = normalizeScopePathInput(trimmedPath);

    if (
      seenPaths.has(normalizedPath) ||
      reservedPaths.includes(normalizedPath)
    ) {
      return false;
    }

    seenPaths.add(normalizedPath);
  }

  return true;
};

const createRowId = () =>
  `path-row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const createEmptyPathDraftRow = (): PathDraftRowState => ({
  id: createRowId(),
  path: '',
  kind: null,
  ...scopeFlagsFromMode('attack'),
});

export const createEditPathDraftRow = ({
  path,
  kind,
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: {
  path: string;
  kind: PathScopeKind;
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
}): PathDraftRowState => ({
  id: createRowId(),
  path,
  kind,
  ...resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  }),
});
